import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';
import Button, { ButtonProps } from '@alltrails/denali/components/Button';
import type { IconDefinition } from '@alltrails/denali/components/IconRenderer';
import { useExperiments } from '@alltrails/experiments';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useDeeplink from '../../hooks/useDeeplink';
import { deepLinkRedirect } from '../../utils';
import AppPromotion from '../AppPromotion';
import BaseInstallInterstitial from '../BaseInstallInterstitial';
import FeatureContext from './FeatureContext';
import styles from './MiniInstallInterstitial.module.scss';

type BaseMiniInstallInterstitialProps = {
  contentsClassName?: string;
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: InstallPromptTrigger;
  deeplinkData?: DeeplinkData;
  customAppPath?: string;
  buttonText?: ReactNode;
  secondaryAction?: {
    text: ReactNode;
    onClick: () => void;
    icon?: ButtonProps['icon'];
  };
  shouldHideCloseButton?: boolean;
  clickOutsideDeactivates?: boolean;
};

type FeatureContextVariantProps = BaseMiniInstallInterstitialProps & {
  variant: 'feature-context';
  trailDetails: TrailDetails;
  featureIcon: IconDefinition<'size'>;
  heading?: ReactNode;
  description?: ReactNode;
};

type AppInfoVariantProps = BaseMiniInstallInterstitialProps & {
  variant?: 'app-info';
  trailDetails?: never;
};

type MiniInstallInterstitialProps = FeatureContextVariantProps | AppInfoVariantProps;

const MiniInstallInterstitial = ({
  contentsClassName,
  isOpen = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  deeplinkData,
  customAppPath,
  buttonText,
  secondaryAction,
  shouldHideCloseButton = false,
  clickOutsideDeactivates = true,
  ...variantProps
}: MiniInstallInterstitialProps) => {
  const intl = useIntl();
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const getDeeplink = useDeeplink({ deeplinkData, customAppPath, isOpen, promptType: 'interstitial', displayTrigger });

  const handleContinueInApp = () => {
    deepLinkRedirect(getDeeplink(), 'interstitial', promptLocation, isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native', displayTrigger);
  };

  return (
    <BaseInstallInterstitial
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      promptLocation={promptLocation}
      displayTrigger={displayTrigger}
      shouldHideCloseButton={shouldHideCloseButton}
      clickOutsideDeactivates={clickOutsideDeactivates}
    >
      <div
        className={classNames(styles.content, contentsClassName, {
          [styles.featureContextContent]: variantProps.variant === 'feature-context',
          [styles.topPadding]: variantProps.variant === 'feature-context' && shouldHideCloseButton
        })}
      >
        {variantProps.variant === 'feature-context' ? (
          <FeatureContext
            trailDetails={variantProps.trailDetails}
            heading={variantProps.heading}
            description={variantProps.description}
            featureIcon={variantProps.featureIcon}
          />
        ) : (
          <AppPromotion heading={<FormattedMessage defaultMessage="AllTrails is better in the app" />} />
        )}

        <div className={styles.actions}>
          {secondaryAction && (
            <Button
              icon={secondaryAction.icon}
              onClick={secondaryAction.onClick}
              text={secondaryAction.text}
              variant="default"
              width="fullOnMobile"
            />
          )}
          <Button
            width="fullOnMobile"
            variant="primary"
            text={buttonText || intl.formatMessage({ defaultMessage: 'Continue in app' })}
            onClick={handleContinueInApp}
          />
        </div>
      </div>
    </BaseInstallInterstitial>
  );
};

export default MiniInstallInterstitial;
